'use-strict';

import { CONST } from 'Util/util';
import VZSpinner from 'Components/VZSpinner';
import { handleTransition } from 'Util/transitions';
import { showErrorModal } from 'Root/modal';

class ProductListingControls {
    constructor() {
        // Filter
        this.selectedFilters = [];
        this.isFilterBarSticky = false;
        this.mdBreakpoint = parseInt(CONST.breakpoints.lg, 10);

        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();

        this.initializeFilterBar();
        this.initializeSorting();
        this.initializeShowMore();

        window.addEventListener('resize', () => this.updateFilterBarHeight());
        window.dataLayer = window.dataLayer || [];
    }

    /**
     * @function getElements
     * @private
     * @description Gets the necessary DOM elements and stores them as class properties
     */
    getElements() {
        this.productList = document.querySelector('.js-product-listing');
        this.filterBar = document.querySelector('.js-filter-bar');
    }

    /**
     * @function initialize
     * @private
     * @description initializes the file
     */
    initializeFilterBar() {
        this.getElements();

        // Filter Bar is not configured correctly
        if (!this.filterBar) return;

        this.filterBarWrapper = document.querySelector('.js-filter-bar-wrapper');

        this.filters = this.filterBar.querySelectorAll('.js-filter');
        this.filtersWrapper = this.filterBar.querySelector('.js-filter-bar-content');
        this.allProductsFilter = this.filterBar.querySelector('.js-filter-reset');
        this.getElements();

        this.updateFilterBarHeight();

        // Bind the click event to the 'all products' button
        if (this.allProductsFilter) {
            this.allProductsFilter.addEventListener('click', () => {
                this.clearAllFilters();
                this.allProductsFilter.classList.add('active');
                this.selectedFilters = [];
                this.updateSelectedFiltersDataAttribute();
                this.applyChanges(this.allProductsFilter.getAttribute('data-href'));
            });
        }

        // Bind the click event to the rest of the filter buttons
        this.filters.forEach((filter) => {
            if (filter !== this.allProductsFilter) {
                filter.addEventListener('click', () => {
                    this.toggleFilter(filter, Array.from(this.filters).indexOf(filter));
                });
            }
        });

        if (this.allProductsFilter && !Array.from(this.filters).some((filter) => filter.classList.contains('active'))) {
            this.allProductsFilter.classList.add('active');
        }

        // Scroll event listeners
        this.filtersWrapper.addEventListener('scroll', () => this.handleFilterScroll());
        this.showFadingEffect();

        if (this.filterBarWrapper && this.filterBarWrapper.classList.contains('js-filter-bar-sticky')) {
            window.addEventListener('scroll', () => this.handleScroll());
        }
    }

    /**
     * @function initializeSorting
     * @private
     * @description Initializes the sorting functionality
     */
    initializeSorting() {
        this.sorting = document.querySelector('.js-filter-bar-sort');

        // Sorting is not configured correctly
        if (!this.sorting) return;

        // Remove existing listener if it exists
        if (this.sortingChangeHandler) {
            this.sorting.removeEventListener('change', this.sortingChangeHandler);
        }

        // Create and store the handler function
        this.sortingChangeHandler = (event) => {
            const selectedOption = event.target.options[event.target.selectedIndex];
            const url = selectedOption.value;
            this.applyChanges(url);
        };

        this.sorting.addEventListener('change', this.sortingChangeHandler);
    }

    /**
     * @function initializeShowMore
     * @private
     * @description Initializes the show more functionality
     */
    initializeShowMore() {
        const showMoreButton = document.querySelector('.js-show-more-button');
        if (showMoreButton) {
            showMoreButton.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                this.handleShowMore(e);
            });
        }
    }

    /**
     * @function handleShowMore
     * @private
     * @param {Event} e - The click event
     * @description Handles the show more button click to load additional products
     */
    handleShowMore(e) {
        const showMoreUrl = e.target.getAttribute('data-url');
        if (!showMoreUrl) return;

        const gridFooter = document.querySelector('.js-grid-footer');
        const errorData = gridFooter ? JSON.parse(gridFooter.getAttribute('data-general-error')) : null;

        this.spinner.start();

        fetch(showMoreUrl)
            .then((response) => {
                if (!response.ok) {
                    if (errorData) {
                        showErrorModal(errorData.title, errorData.body, errorData.cta);
                    }
                    this.spinner.stop();
                    throw new Error('Error');
                }
                return response.text();
            })
            .then((response) => {
                const parser = new DOMParser();
                const results = parser.parseFromString(response, 'text/html');

                // Append new products to the product list
                const newProducts = results.querySelectorAll('.js-product-tile');
                const productList = document.querySelector('.js-product-listing');
                if (productList && newProducts.length > 0) {
                    newProducts.forEach((product) => {
                        productList.appendChild(product);
                    });
                }

                // Replace the grid-footer with the new one
                const currentGridFooter = document.querySelector('.js-grid-footer');
                const newGridFooter = results.querySelector('.js-grid-footer');
                if (currentGridFooter && newGridFooter) {
                    handleTransition(() => {
                        currentGridFooter.replaceWith(newGridFooter);
                    });

                    // Re-initialize show more button if it exists
                    const newShowMoreButton = newGridFooter.querySelector('.js-show-more-button');
                    if (newShowMoreButton) {
                        newShowMoreButton.addEventListener('click', (event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            this.handleShowMore(event);
                        });
                    }
                }

                // Update URL with permalink from response
                const newPermalink = results.querySelector('.permalink')?.value;
                if (newPermalink) {
                    const seoPermalinkInput = document.querySelector('.seoPermalink');
                    if (seoPermalinkInput) seoPermalinkInput.value = newPermalink;
                    ProductListingControls.updateBrowserURL();
                }
                this.spinner.stop();
            })
            .catch(() => {
                this.spinner.stop();
                if (errorData) {
                    showErrorModal(errorData.title, errorData.body, errorData.cta);
                }
            });
    }

    /**
     * @function updateDom
     * @private
     * @param {Object} results - The parsed HTML response from the fetch request
     * @param {string} selector - The CSS selector of the DOM element to update
     * @description Updates the specified DOM element with the parsed HTML response
     * @returns {Promise} promise - Resolves when the DOM update is complete
     */
    static updateDom(results, selector) {
        return new Promise((resolve) => {
            const updates = results.querySelector(selector);
            const target = document.querySelector(selector);

            // If elements not found, resolve anyway (element might not be in response)
            if (!updates || !target) {
                resolve();
                return;
            }

            if (document.startViewTransition) {
                const transition = document.startViewTransition(() => {
                    target.innerHTML = updates.innerHTML;
                });

                transition.finished.then(resolve);
            } else {
                target.innerHTML = updates.innerHTML;
                resolve();
            }
        });
    }

    /**
     * @function updateBrowserURL
     * @static
     * @description Updates the browser URL with the value of the 'seoPermalink' from the document
     */
    static updateBrowserURL() {
        const url = document.querySelector('.seoPermalink')?.value;
        if (url) {
            window.history.pushState({ urlPath: url }, '', url);
        }
    }

    /**
     * @function parseResults
     * @private
     * @param {Object} response - The response from the fetch request
     * @description Updates the DOM with the parsed HTML response and re-initializes the event listeners on the DOM elements
     */
    parseResults(response) {
        const parser = new DOMParser();
        const results = parser.parseFromString(response, 'text/html');
        const elementsToUpdate = ['.js-product-listing', '.js-filter-bar-wrapper', '.grid-footer'];
        const domUpdatePromises = elementsToUpdate.map((selector) => ProductListingControls.updateDom(results, selector));

        Promise.all(domUpdatePromises).then(() => {
            this.initializeFilterBar();
            this.initializeSorting();
            this.initializeShowMore();
            ProductListingControls.updateBrowserURL();
            this.spinner.stop();
        });
    }

    /**
     * @function scrollToProductList
     * @private
     * @description Scrolls the window to the product list if it is past the top of the product list
     */
    scrollToProductList() {
        this.getElements();
        if (!this.productList || !this.filterBar) return;

        const filterBarTopOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--filter-bar-top-offset').trim(), 10);
        const offsetHeight = this.filterBar.height + filterBarTopOffset;

        // Calculate the scroll amount and scroll if it is past the top of the product list
        const scrollAmount = this.productList.top - offsetHeight;
        if (scrollAmount < 0) {
            window.scrollBy({
                top: scrollAmount,
                behavior: 'smooth'
            });
        }
    }

    /**
     * @function clearAllFilters
     * @private
     * @description Clear all filters and remove the 'active' class
     */
    clearAllFilters() {
        this.filters.forEach((filter) => {
            filter.classList.remove('active');
        });
    }

    /**
     * @function toggleFilter
     * @private
     * @param {HTMLElement} filter - The filter to toggle
     * @param {number} index - The index of the filter
     * @description Toggle the active state of a filter
     */
    toggleFilter(filter, index) {
        filter.classList.toggle('active');
        this.activeFilters = Array.from(document.getElementsByClassName('filter-bar-btn active')).filter((btn) => !btn.classList.contains('js-filter-reset'));
        if (filter.classList.contains('active')) {
            this.selectedFilters.push(index);
            if (this.allProductsFilter) {
                this.allProductsFilter.classList.remove('active');
            }
        } else {
            this.selectedFilters = this.selectedFilters.filter((i) => i !== index);

            if (this.allProductsFilter && this.selectedFilters.length === 0) {
                this.allProductsFilter.classList.add('active');
            }
        }
        this.activeFiltersType = '';
        if (this.activeFilters.length > 0) {
            this.activeFiltersType = Array.from(this.activeFilters)
                .map((activeFilter) => activeFilter.getAttribute('data-value'))
                .join('_');
        }

        this.updateSelectedFiltersDataAttribute();
        this.applyChanges(filter.getAttribute('data-href'));

        window.dataLayer.push({
            event: 'mkb_solutions_filter',
            solutions_filter: this.activeFiltersType
        });
    }

    /**
     * @function updateSelectedFiltersDataAttribute
     * @private
     * @description Update the selected filters data attribute on the filter bar element
     */
    updateSelectedFiltersDataAttribute() {
        const value = this.selectedFilters.length > 0 ? this.selectedFilters.toString() : '';
        this.filterBar.setAttribute('data-selected-filters', value);
    }

    /**
     * @function handleFilterScroll
     * @private
     * @description Handles the horizontal scrolling event for the filters wrapper to show/hide the fading effect.
     */
    handleFilterScroll() {
        this.showFadingEffect();
    }

    /**
     * @function handleScroll
     * @private
     * @description Handles the global (vertical) scrolling event to check the sticky state of the filter bar.
     */
    handleScroll() {
        this.checkIfFilterBarIsSticky();
    }

    /**
     * @function checkIfFilterBarIsSticky
     * @private
     * @description Checks if the filter bar is in a sticky state based on its current position.
     */
    checkIfFilterBarIsSticky() {
        const style = window.getComputedStyle(this.filterBarWrapper);
        if (style.position !== 'sticky') {
            this.filterBarWrapper.classList.remove('is-sticky');
            return;
        }

        const rect = this.filterBarWrapper.getBoundingClientRect();
        const filterBarTopOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--filter-bar-top-offset').trim(), 10);
        const isCurrentlySticky = rect.top <= filterBarTopOffset;

        if (isCurrentlySticky !== this.isFilterBarSticky) {
            this.isFilterBarSticky = isCurrentlySticky;
            this.filterBarWrapper.classList.toggle('is-sticky', this.isFilterBarSticky);
        }
    }

    /**
     * @function showFadingEffect
     * @private
     * @description Toggles the fading effect classes based on the scroll state of the filters wrapper.
     */
    showFadingEffect() {
        const { scrollLeft, clientWidth, scrollWidth } = this.filtersWrapper;

        const overflowLeft = scrollLeft > 0;
        const overflowRight = scrollWidth - scrollLeft > clientWidth;

        this.filterBar.classList.toggle('fade-left', overflowLeft);
        this.filterBar.classList.toggle('fade-right', overflowRight);
    }

    /**
     * @function updateFilterBarHeight
     * @private
     * @description Updates the height of the filter bar based on its bounding rectangle
     */
    updateFilterBarHeight() {
        if (this.filterBar) this.filterBar.height = this.filterBar.getBoundingClientRect().height;
    }

    /**
     * @function applyChanges
     * @private
     * @param {string} url - The URL to fetch data from
     * @description Fetches the data from the provided URL and applies the changes (filtering or sorting)
     */
    applyChanges(url) {
        const gridFooter = document.querySelector('.js-grid-footer');
        const errorData = gridFooter ? JSON.parse(gridFooter.getAttribute('data-general-error')) : null;

        this.spinner.start();

        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    if (errorData) {
                        showErrorModal(errorData.title, errorData.body, errorData.cta);
                    }
                    this.spinner.stop();
                    throw new Error('Error');
                }
                return response.text();
            })
            .then((response) => {
                const parser = new DOMParser();
                const results = parser.parseFromString(response, 'text/html');

                return ProductListingControls.updateDom(results, '.js-product-listing').then(() => this.parseResults(response));
            })
            .then(() => {
                // Update browser URL after filter/sort changes (pagination resets)
                ProductListingControls.updateBrowserURL();
                this.scrollToProductList();
                this.spinner.stop();
            })
            .catch(() => {
                this.spinner.stop();
                if (errorData) {
                    showErrorModal(errorData.title, errorData.body, errorData.cta);
                }
            });
    }
}

(() => new ProductListingControls())();
